import { useCallback, useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { PROPHET21_APP_ID, PROPHET21_PROVIDER_ID } from "@forgepoint/shared";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useConnectionsStore } from "@/store/connectionsStore";
import { toast } from "sonner";

type Prophet21AuthMode = "consumerKey" | "password";

interface Prophet21ConnectionDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: (connectionId: string) => void;
}

/** Setup dialog that validates and stores an Epicor Prophet 21 connection. */
export function Prophet21ConnectionDialog({
  open,
  onOpenChange,
  onSuccess,
}: Prophet21ConnectionDialogProps) {
  const [baseUrl, setBaseUrl] = useState("");
  const [companyId, setCompanyId] = useState("");
  const [authMode, setAuthMode] = useState<Prophet21AuthMode>("consumerKey");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [consumerKey, setConsumerKey] = useState("");
  const [requiresEgressProxy, setRequiresEgressProxy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const finalizeConnection = useConnectionsStore(
    (state) => state.finalizeConnection,
  );
  const fetchConnectionsByApp = useConnectionsStore(
    (state) => state.fetchConnectionsByApp,
  );

  useEffect(() => {
    if (!open) {
      setBaseUrl("");
      setCompanyId("");
      setAuthMode("consumerKey");
      setUsername("");
      setPassword("");
      setConsumerKey("");
      setRequiresEgressProxy(false);
      setError(null);
      setIsSubmitting(false);
    }
  }, [open]);

  const handleSubmit = useCallback(async () => {
    const normalizedBaseUrl = baseUrl.trim().replace(/\/+$/, "");
    try {
      new URL(normalizedBaseUrl);
    } catch {
      setError("Provide a valid Prophet 21 middleware base URL.");
      return;
    }
    if (!companyId) {
      setError("Provide the Prophet 21 company id.");
      return;
    }
    if (authMode === "consumerKey" && !consumerKey) {
      setError("Provide the Prophet 21 consumer key.");
      return;
    }
    if (authMode === "password" && (!username || !password)) {
      setError("Provide the Prophet 21 username and password.");
      return;
    }
    setError(null);
    setIsSubmitting(true);
    try {
      const connection = await finalizeConnection({
        connectionProviderId: PROPHET21_PROVIDER_ID,
        appId: PROPHET21_APP_ID,
        credentials:
          authMode === "consumerKey"
            ? {
                type: "Prophet21ServiceCredentials",
                authMode: "consumerKey",
                consumerKey,
                ...(username ? { username } : {}),
              }
            : {
                type: "Prophet21ServiceCredentials",
                authMode: "password",
                username,
                password,
              },
        config: { baseUrl: normalizedBaseUrl, companyId },
        requiresEgressProxy,
        name: "Epicor Prophet 21",
      });
      if (!connection) {
        const finalizeError = useConnectionsStore
          .getState()
          .connectionStates.get(PROPHET21_APP_ID)?.finalizeError;
        setError(
          finalizeError ||
            "Prophet 21 connection validation failed. Check the credentials and URL.",
        );
        return;
      }
      toast.success("Prophet 21 connection created successfully.");
      await fetchConnectionsByApp(PROPHET21_APP_ID);
      onSuccess?.(connection.id);
      onOpenChange(false);
    } catch (submitError) {
      setError(
        submitError instanceof Error
          ? submitError.message
          : "Failed to create Prophet 21 connection.",
      );
    } finally {
      setIsSubmitting(false);
    }
  }, [
    baseUrl,
    companyId,
    authMode,
    username,
    password,
    consumerKey,
    requiresEgressProxy,
    fetchConnectionsByApp,
    finalizeConnection,
    onOpenChange,
    onSuccess,
  ]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Connect Epicor Prophet 21</DialogTitle>
          <DialogDescription>
            Enter the Prophet 21 middleware URL, company, and API credentials.
            The UI-server address is discovered automatically.
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 py-4">
          <div className="grid gap-2">
            <Label htmlFor="prophet21-base-url">Base URL</Label>
            <Input
              id="prophet21-base-url"
              type="url"
              value={baseUrl}
              onChange={(event) => setBaseUrl(event.target.value)}
              placeholder="https://p21.example.com:3443"
            />
          </div>
          <div className="grid gap-2">
            <Label htmlFor="prophet21-company-id">Company ID</Label>
            <Input
              id="prophet21-company-id"
              value={companyId}
              onChange={(event) => setCompanyId(event.target.value)}
              placeholder="1"
            />
          </div>
          <Tabs
            value={authMode}
            onValueChange={(nextMode) => {
              setAuthMode(nextMode === "password" ? "password" : "consumerKey");
            }}
          >
            <TabsList className="grid grid-cols-2">
              <TabsTrigger value="consumerKey">Consumer key</TabsTrigger>
              <TabsTrigger value="password">
                Username &amp; password
              </TabsTrigger>
            </TabsList>
            <TabsContent value="consumerKey" className="grid gap-4 pt-4">
              <div className="grid gap-2">
                <Label htmlFor="prophet21-consumer-key">Consumer key</Label>
                <Input
                  id="prophet21-consumer-key"
                  type="password"
                  value={consumerKey}
                  onChange={(event) => setConsumerKey(event.target.value)}
                  autoComplete="off"
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="prophet21-service-username">
                  Service username (optional)
                </Label>
                <Input
                  id="prophet21-service-username"
                  value={username}
                  onChange={(event) => setUsername(event.target.value)}
                  autoComplete="off"
                />
              </div>
            </TabsContent>
            <TabsContent value="password" className="grid gap-4 pt-4">
              <div className="grid gap-2">
                <Label htmlFor="prophet21-username">Username</Label>
                <Input
                  id="prophet21-username"
                  value={username}
                  onChange={(event) => setUsername(event.target.value)}
                  autoComplete="username"
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="prophet21-password">Password</Label>
                <Input
                  id="prophet21-password"
                  type="password"
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  autoComplete="current-password"
                />
              </div>
            </TabsContent>
          </Tabs>
          <div className="flex items-center justify-between gap-4">
            <div className="grid gap-1">
              <Label htmlFor="prophet21-egress-proxy">
                Require static egress IP
              </Label>
              <p className="text-sm text-muted-foreground">
                Route all requests for this connection through Forgepoint's
                allowlistable IP.
              </p>
            </div>
            <Switch
              id="prophet21-egress-proxy"
              checked={requiresEgressProxy}
              onCheckedChange={setRequiresEgressProxy}
            />
          </div>
          {error && <p className="text-sm text-red-500">{error}</p>}
        </div>
        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button type="button" onClick={handleSubmit} disabled={isSubmitting}>
            Connect
            {isSubmitting && <Loader2 className="ml-2 h-4 w-4 animate-spin" />}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
